// navigation-completion Stage C/E — Inspector → Documents → Navigation regional
// ASSESSMENT panel.
//
// Shows which ICH/regional navigation criteria APPLY to this document's current
// artifact and each criterion's status — with its regulator severity, company
// release treatment, "Applies to" label and recorded-exception disposition shown
// SEPARATELY. It never re-states a regional rule (the server does the evaluating)
// and never launders a failure into a pass:
//   • An exempted failure is "Failed — exempted (authorised departure)", amber,
//     never green (handled by the reused NavigationExceptionControls too).
//   • An unknown / not-evaluated / unsupported result shows its honest reason.
//   • An AutoeCTD internal-quality signal is tagged as such, distinct from a
//     sourced regulator obligation.
//   • Only an APPLICABLE, WAIVABLE (regional) FAILURE offers an exception form;
//     structural integrity is non-waivable and offers none.
//
// The exception request/approve/revoke actions reuse the shared
// NavigationExceptionControls; permissions come from the live RBAC lookup
// (Documents:write to request/revoke, Documents:sign to approve). A mutation
// reloads the assessment because an exemption changes a criterion's disposition.

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { parseApiErrorBody } from '@/api/v2Client';
import {
  navigationAssessment,
  type AssessedCriterionWire,
  type AssessedProfileWire,
  type NavigationAssessmentResult,
} from '@/api/navigationAssessment';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { NavigationExceptionControls } from '@/components/settings/NavigationExceptionControls';
import { navStyles } from './navShared';

export interface NavigationAssessmentProps {
  readonly documentId: string;
  readonly submissionId: string;
  /** The revision the host (NavigationTab) last saw — used as a coherence precondition. */
  readonly revision: number;
  /** The artifact hash the host last saw — used as a coherence precondition. */
  readonly artifactSha256: string;
  /** Optional policy-version coherence precondition. */
  readonly policyVersion?: number;
}

type ChipTone = 'pass' | 'fail' | 'exempted' | 'unknown' | 'neutral';

const chipColors: Record<ChipTone, { bg: string; fg: string }> = {
  pass: { bg: 'var(--ok-bg, #e7f6ec)', fg: 'var(--ok, #15803d)' },
  fail: { bg: 'var(--red-bg, #fdecee)', fg: 'var(--red, #b00020)' },
  // An exempted FAILURE is amber — a recorded, authorised departure, never green.
  exempted: { bg: 'var(--amber, #e0b000)', fg: '#1a1a1a' },
  unknown: { bg: 'var(--amber-bg, #fff8e1)', fg: '#7a5a00' },
  neutral: { bg: 'var(--border, #e5e5e5)', fg: '#555' },
};

const tagStyle: CSSProperties = {
  display: 'inline-block',
  fontSize: 10,
  fontWeight: 700,
  padding: '1px 6px',
  borderRadius: 3,
  marginRight: 6,
};

function chipFor(c: AssessedCriterionWire): { label: string; tone: ChipTone } {
  if (c.status === 'pass') return { label: 'Pass', tone: 'pass' };
  if (c.status === 'fail') {
    if (c.exemption.kind === 'exempted') {
      return { label: 'Failed — exempted (authorised departure)', tone: 'exempted' };
    }
    return { label: 'Fail', tone: 'fail' };
  }
  if (c.status === 'unknown') return { label: 'Unknown — not certified', tone: 'unknown' };
  if (c.status === 'not-evaluated') return { label: 'Not evaluated', tone: 'neutral' };
  return { label: 'Not applicable', tone: 'neutral' };
}

function severityLabel(c: AssessedCriterionWire): string | null {
  if (!c.severity) return null;
  const authority =
    c.authorityLevel === 'formal-validation' ? 'validator rule' : c.authorityLevel === 'guidance' ? 'guidance' : null;
  const sev = c.severity === 'error' ? 'Error' : c.severity === 'warning' ? 'Warning' : 'Recommendation';
  return authority ? `Regulator: ${sev} (${authority})` : `Regulator: ${sev}`;
}

function companyLabel(c: AssessedCriterionWire): string | null {
  if (!c.companyTreatment) return null;
  switch (c.companyTreatment) {
    case 'must-fix':
      return 'Company: must fix before release';
    case 'advisory':
      return 'Company: advisory';
    case 'accept':
      return 'Company: accepted';
  }
}

function CriterionRow({ c }: { readonly c: AssessedCriterionWire }): ReactElement {
  const chip = chipFor(c);
  const colors = chipColors[chip.tone];
  const sev = c.internalQuality ? null : severityLabel(c);
  const company = companyLabel(c);
  return (
    <div
      data-testid={`assessment-criterion-${c.profile ?? 'structural'}-${c.criterionId}`}
      style={{ borderTop: '1px solid var(--border, #eee)', padding: '8px 0' }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
        <span
          data-testid={`assessment-status-${c.profile ?? 'structural'}-${c.criterionId}`}
          style={{ ...tagStyle, background: colors.bg, color: colors.fg }}
        >
          {chip.label}
        </span>
        <span style={{ ...navStyles.value, fontWeight: 600 }}>{c.title}</span>
      </div>
      <div style={{ ...navStyles.muted, marginTop: 2 }}>
        {c.internalQuality ? 'AutoeCTD quality check' : c.appliesToLabel ?? (c.kind === 'structural-integrity' ? 'Applies to: structural integrity (non-waivable)' : null)}
      </div>
      <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {sev && <span style={{ ...tagStyle, background: 'var(--surface2, #f1f1f1)', color: 'var(--text, #222)' }}>{sev}</span>}
        {company && (
          <span style={{ ...tagStyle, background: 'var(--surface2, #f1f1f1)', color: 'var(--text, #222)' }}>{company}</span>
        )}
        {c.internalQuality && (
          <span
            data-testid={`assessment-internal-${c.criterionId}`}
            style={{ ...tagStyle, background: 'var(--surface2, #eef)', color: 'var(--text-muted, #555)' }}
            title="An AutoeCTD internal-quality signal — not a sourced regulator obligation."
          >
            App quality check
          </span>
        )}
        {!c.enabled && (
          <span style={{ ...tagStyle, background: 'var(--surface2, #f1f1f1)', color: 'var(--text-muted, #555)' }}>
            Disabled by policy
          </span>
        )}
      </div>
      {c.detail && <div style={{ ...navStyles.muted, marginTop: 4 }}>{c.detail}</div>}
      {c.unknownReason && (
        <div style={{ ...navStyles.muted, marginTop: 2, fontStyle: 'italic' }} data-testid={`assessment-unknown-${c.criterionId}`}>
          Why unknown: {c.unknownReason}
        </div>
      )}
      {c.source && !c.internalQuality && (
        <details style={{ marginTop: 4 }}>
          <summary style={{ ...navStyles.label, cursor: 'pointer', fontWeight: 500 }}>Source</summary>
          <div style={{ ...navStyles.muted, marginTop: 4 }}>
            {c.source.authority} — {c.source.specification} {c.source.version}
            {c.source.effectiveDate ? ` (effective ${c.source.effectiveDate})` : ''}
            {c.source.section ? ` · ${c.source.section}` : ''}
            {c.source.url && (
              <>
                {' '}
                <a href={c.source.url} target="_blank" rel="noopener noreferrer">
                  reference
                </a>
              </>
            )}
          </div>
        </details>
      )}
    </div>
  );
}

export function NavigationAssessment(props: NavigationAssessmentProps): ReactElement {
  const { documentId, submissionId, revision, artifactSha256, policyVersion } = props;
  const perms = useUserPermissions();
  const [result, setResult] = useState<NavigationAssessmentResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const loadEpoch = useRef(0);
  useEffect(() => () => { loadEpoch.current += 1; }, []);

  const load = useCallback(
    async (coherent: boolean): Promise<void> => {
      const epoch = ++loadEpoch.current;
      setLoading(true);
      setError(null);
      setResult(null);
      try {
        const pre = coherent
          ? {
              expectedRevision: revision,
              expectedArtifactSha256: artifactSha256,
              ...(policyVersion !== undefined ? { expectedPolicyVersion: policyVersion } : {}),
            }
          : undefined;
        const next = await navigationAssessment.get(documentId, pre);
        if (epoch === loadEpoch.current) setResult(next);
      } catch (err) {
        if (epoch !== loadEpoch.current) return;
        const body = parseApiErrorBody(err);
        setError(body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)));
      } finally {
        if (epoch === loadEpoch.current) setLoading(false);
      }
    },
    [documentId, revision, artifactSha256, policyVersion],
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  // A mutation (request/approve/revoke) does not change bytes, so reload with the
  // same coherence preconditions to pick up the new exemption disposition.
  const reload = useCallback(() => void load(true), [load]);

  const canRequest = perms.hasPermission('Documents', 'write');
  const canApprove = perms.hasPermission('Documents', 'sign');
  const canRevoke = perms.hasPermission('Documents', 'write');

  // Bind exceptions to the exact assessed artifact; an inconsistent response
  // disables requests until the reviewer reloads the current document.
  const bindingWarning = useMemo(
    () => (result && result.status === 'assessed' && !result.exceptionBindingMatchesAssessment ? true : false),
    [result],
  );

  if (loading && !result) {
    return (
      <div style={navStyles.muted} data-testid="assessment-loading">
        Loading navigation criteria assessment…
      </div>
    );
  }
  if (error && !result) {
    return (
      <div style={navStyles.error} role="alert" data-testid="assessment-error">
        Could not load the navigation assessment: {error}
      </div>
    );
  }
  if (!result) return <div data-testid="assessment-empty" style={navStyles.muted} />;

  // --- Non-assessable statuses: honest, never a verdict --------------------
  if (result.status === 'conflict') {
    return (
      <section style={navStyles.section} data-testid="assessment-conflict">
        <h4 style={navStyles.heading}>Navigation assessment is out of date</h4>
        <div style={navStyles.muted}>{result.detail}</div>
        <button type="button" style={{ ...navStyles.smallBtn, marginTop: 8 }} data-testid="assessment-reload" onClick={() => void load(false)}>
          Load the latest assessment
        </button>
      </section>
    );
  }
  if (result.status === 'unsupported') {
    return (
      <section style={navStyles.section} data-testid="assessment-unsupported">
        <h4 style={navStyles.heading}>Regional navigation criteria not assessed</h4>
        <div style={navStyles.muted}>{result.unsupportedReason}</div>
        <div style={{ ...navStyles.muted, marginTop: 6 }}>
          No criterion is reported as passing — the artifact could not be assessed as a PDF.
        </div>
      </section>
    );
  }
  if (result.status === 'corrupt') {
    return (
      <section style={navStyles.section} data-testid="assessment-corrupt">
        <h4 style={navStyles.heading}>Navigation assessment unavailable</h4>
        <div style={navStyles.error} role="alert">
          {result.detail}
        </div>
      </section>
    );
  }

  // --- status === 'assessed' ------------------------------------------------
  return (
    <div data-testid="navigation-assessment">
      <section style={navStyles.section} data-testid="assessment-header">
        <h4 style={navStyles.heading}>Navigation criteria assessment</h4>
        <div style={navStyles.muted} data-testid="assessment-source">
          Assessed: {result.source.label}. {result.region ? `Filing region: ${result.region}.` : 'Filing region not set.'}
        </div>
        <div
          style={{ ...navStyles.muted, marginTop: 4, color: result.qualification.eligible ? 'var(--ok, #15803d)' : 'var(--warning, #b45309)' }}
          data-testid="assessment-qualification"
        >
          {result.qualification.eligible
            ? 'Automatic navigation output is qualified for release.'
            : 'Any generated/edited navigation is review-only and not release-eligible until qualification completes.'}
        </div>
        {result.coverage.remoteVerificationOmitted && (
          <div style={{ ...navStyles.muted, marginTop: 4 }} data-testid="assessment-coverage">
            {result.coverage.note}
          </div>
        )}
        {result.coverage.scanComplete === false && (
          <div style={{ ...navStyles.muted, marginTop: 4 }} data-testid="assessment-scan-incomplete">
            Navigation scan coverage is incomplete, so a clean result cannot be certified as a pass.
          </div>
        )}
        {bindingWarning && (
          <div style={{ ...navStyles.note, color: 'var(--warning, #b45309)', marginTop: 6 }} data-testid="assessment-binding-warning">
            The exception identity does not match the assessed document. Refresh this assessment before
            requesting an exception; no approval will be recorded against different bytes.
          </div>
        )}
      </section>

      {/* Structural integrity — non-waivable */}
      {result.structural.length > 0 && (
        <section style={navStyles.section} data-testid="assessment-structural">
          <h4 style={navStyles.heading}>Structural integrity (non-waivable)</h4>
          {result.structural.map((c) => (
            <CriterionRow key={`structural-${c.criterionId}`} c={c} />
          ))}
        </section>
      )}

      {/* One section per evaluated / requested profile */}
      {result.profiles.map((p) => (
        <ProfileSection
          key={`${p.requestedProfile}-${p.role}`}
          profile={p}
          submissionId={submissionId}
          documentId={documentId}
          bindingArtifactSha256={result.artifactSha256}
          bindingRevision={result.revision}
          bindingPolicyVersion={result.policyVersion}
          targetScopeFingerprint={result.targetScopeFingerprint ?? null}
          targetScopeDescription={result.targetScopeDescription ?? 'All verified remote destinations for this exact artifact'}
          canRequest={canRequest && !bindingWarning}
          canApprove={canApprove}
          canRevoke={canRevoke}
          onMutation={reload}
        />
      ))}
    </div>
  );
}

function ProfileSection(props: {
  readonly profile: AssessedProfileWire;
  readonly submissionId: string;
  readonly documentId: string;
  readonly bindingArtifactSha256: string;
  readonly bindingRevision: number;
  readonly bindingPolicyVersion: number;
  readonly targetScopeFingerprint: string | null;
  readonly targetScopeDescription: string;
  readonly canRequest: boolean;
  readonly canApprove: boolean;
  readonly canRevoke: boolean;
  readonly onMutation: () => void;
}): ReactElement {
  const { profile: p, submissionId, documentId, bindingArtifactSha256, bindingRevision, bindingPolicyVersion, targetScopeFingerprint, targetScopeDescription, canRequest, canApprove, canRevoke, onMutation } = props;

  const heading = p.meta ? `${p.meta.label} ${p.meta.version}` : p.requestedProfile;

  return (
    <section style={navStyles.section} data-testid={`assessment-profile-${p.requestedProfile}`}>
      <h4 style={navStyles.heading}>
        {heading} <span style={navStyles.muted}>({p.role === 'target' ? 'target profile' : 'preparation profile'})</span>
      </h4>
      {p.meta && (
        <div style={navStyles.muted}>
          {p.meta.authority} — {p.meta.specification}
          {p.meta.url && (
            <>
              {' · '}
              <a href={p.meta.url} target="_blank" rel="noopener noreferrer">
                source
              </a>
            </>
          )}
        </div>
      )}

      {!p.evaluated ? (
        <div style={{ ...navStyles.muted, marginTop: 6 }} data-testid={`assessment-not-evaluated-${p.requestedProfile}`}>
          {p.advisory ?? 'This profile is not assessed; it never produces a pass.'}
          {p.inheritsFrom ? ` (documented inheritance from ${p.inheritsFrom}).` : ''}
        </div>
      ) : (
        p.results.map((c) => (
          <div key={`${p.requestedProfile}-${c.criterionId}`}>
            <CriterionRow c={c} />
            {/* Only an applicable, waivable FAILURE offers an exception form. */}
            {c.status === 'fail' && c.waivable && c.profile && (
              <div style={{ margin: '4px 0 10px 0' }} data-testid={`assessment-exception-${p.requestedProfile}-${c.criterionId}`}>
                {c.criterionId === 'nav.remote-destinations' && !targetScopeFingerprint && <div style={navStyles.muted}>Remote target evidence is unavailable. Refresh the assessment after resolving the target context.</div>}
                <NavigationExceptionControls
                  submissionId={submissionId}
                  scope={{ documentId, criterionId: c.criterionId, profile: c.profile }}
                  requestContext={{
                    documentId,
                    criterionId: c.criterionId,
                    profile: c.profile,
                    // The ASSESSED artifact hash — never a source-hash fallback.
                    artifactSha256: bindingArtifactSha256,
                    expectedRevision: bindingRevision,
                    expectedPolicyVersion: bindingPolicyVersion,
                    ...(c.criterionId === 'nav.remote-destinations' ? { targetScopeDescription, ...(targetScopeFingerprint ? { expectedTargetScopeFingerprint: targetScopeFingerprint } : {}) } : {}),
                  }}
                  canRequest={canRequest && (c.criterionId !== 'nav.remote-destinations' || targetScopeFingerprint !== null)}
                  canApprove={canApprove}
                  canRevoke={canRevoke}
                  onMutation={onMutation}
                />
              </div>
            )}
          </div>
        ))
      )}
    </section>
  );
}

export default NavigationAssessment;
